import * as React from 'react'
import { PaymentForm } from '@oneblink/apps-react'
import { paymentService } from '@oneblink/apps-react'
import { SubmissionTypes } from '@oneblink/types'
import { useHistory, useLocation } from 'react-router-dom'
import styled from 'styled-components'
import { Error as _ErrorIcon } from '@styled-icons/material'

import { P } from '../../components/TextComponents'
import LoadingSpinner from '../../components/LoadingSpinner'
import useSanitisedParams from '../../hooks/useSanitisedParams'
import Dialog from '../../components/Dialog'
import useQuery from '../../hooks/useQuery'
import { postRequest } from '../../lib/http'
import config from '../../config'
import { useAppConfig } from '../../providers/AppConfigProvider'

const PaymentHeading = styled.h1<{ $centered?: boolean }>`
  font-size: 28px;
  padding-bottom: ${({ theme }) => theme.unit}px;
  text-align: ${({ $centered }) => ($centered ? 'center' : 'left')};
`
PaymentHeading.displayName = 'PaymentHeading'

const PaymentContainer = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  padding: 0px ${({ theme }) => theme.unit}px;
`
PaymentContainer.displayName = 'PaymentContainer'

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ErrorBox = styled.div`
  margin-top: ${({ theme }) => theme.unit}px;
  margin-bottom: ${({ theme }) => theme.unit}px;
`
ErrorBox.displayName = 'ErrorBox'

const Alert = styled.div`
  margin-bottom: 0.5rem;
  padding: 0.6rem;
  background-color: ${({ theme }) => theme.palette.nswStatusWarningOrangeBg};
  color: ${({ theme }) => theme.palette.nswStatusWarningOrange};
  border: 2px solid ${({ theme }) => theme.palette.nswStatusWarningOrange};
  border-radius: 0.25rem;
  display: flex;
  gap: ${({ theme }) => theme.unit * 0.5}px;
  align-items: center;
  font-size: 0.75rem;
`
Alert.displayName = 'Alert'

const ErrorIcon = styled(_ErrorIcon)<{ $size: 'small' | 'large' }>`
  width: ${({ $size }) => ($size === 'small' ? '20' : '100')}px;
  height: ${({ $size }) => ($size === 'small' ? '20' : '100')}px;
`
ErrorIcon.displayName = 'ErrorIcon'

const ButtonContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.unit}px;
`
ButtonContainer.displayName = 'ButtonContainer'

export default function PaymentDialog() {
  const history = useHistory()
  const location = useLocation()
  const { formId: formSlugOrId } = useSanitisedParams<{ formId: string }>()
  const { formSubmissionPaymentId } = useQuery()
  const { recaptchaKeyType, recaptchaPublicKey } = useAppConfig()

  const [errorMessage, setErrorMessage] = React.useState<string>()
  const [isCancelling, setIsCancelling] = React.useState<boolean>(false)
  const [cancelError, setCancelError] = React.useState<string>()

  const [{ isTryingAgain, tryAgainError }, setTryAgainState] = React.useState<{
    isTryingAgain: boolean
    tryAgainError: Error | null
  }>({ isTryingAgain: false, tryAgainError: null })

  const handleTryAgain = React.useCallback(async () => {
    setTryAgainState({ isTryingAgain: true, tryAgainError: null })
    try {
      const { formSubmissionResult } =
        await paymentService.getFormSubmissionResultPayment()

      if (!formSubmissionResult.payment) {
        throw new Error('Could not retrieve payment info')
      }

      const newPayment = await paymentService.handlePaymentSubmissionEvent({
        formSubmissionResult,
        paymentReceiptUrl: formSubmissionResult.payment.paymentReceiptUrl,
        paymentFormUrl: formSubmissionResult.payment.paymentFormUrl,
      })
      if (newPayment) {
        setErrorMessage(undefined)
        const url = new URL(newPayment.hostedFormUrl)
        history.replace(`${url.pathname}${url.search}`)
      }
    } catch (error) {
      console.warn('Error while attempting to load transaction', error)
      setTryAgainState((current) => ({
        ...current,
        tryAgainError: error as Error,
      }))
    }
    setTryAgainState((current) => ({ ...current, isTryingAgain: false }))
  }, [history])

  const handleCompleted = React.useCallback(
    ({
      formSubmissionPayment,
      paymentReceiptUrl,
    }: {
      formSubmissionPayment: SubmissionTypes.FormSubmissionPayment
      paymentReceiptUrl: string
    }) => {
      if (formSubmissionPayment.status === 'FAILED') {
        if (formSubmissionPayment.type === 'WESTPAC_QUICK_STREAM') {
          return setErrorMessage(
            formSubmissionPayment.paymentTransaction?.responseDescription,
          )
        }
        return setErrorMessage('Payment failed')
      }
      const { searchParams: receiptSearchParams } = new URL(paymentReceiptUrl)
      receiptSearchParams.append(
        'submissionId',
        formSubmissionPayment.submissionId,
      )
      if (formSubmissionPayment.type === 'WESTPAC_QUICK_STREAM') {
        receiptSearchParams.append(
          'amountPaid',
          formSubmissionPayment.paymentTransaction?.principalAmount
            .displayAmount || '',
        )
        receiptSearchParams.append(
          'westpacTotalAmount',
          formSubmissionPayment.paymentTransaction?.totalAmount.displayAmount ||
            '',
        )
      }
      history.replace(
        `/forms/${formSlugOrId}/receipt?${receiptSearchParams.toString()}`,
      )
    },
    [formSlugOrId, history],
  )

  const handleCancel = React.useCallback(async () => {
    setIsCancelling(true)

    try {
      const { formSubmissionResult } =
        await paymentService.getFormSubmissionResultPayment()

      if (!formSubmissionResult.previousFormSubmissionApprovalId) {
        await postRequest({
          url: `${config.HOSTED_API_URL}/cancel-payment`,
          resource: {
            trackingCode: formSubmissionResult.externalId,
            formsAppId: formSubmissionResult.formsAppId,
            formId: formSubmissionResult.definition.id,
            submissionId: formSubmissionResult.submissionId,
          },
          addAuthHeaders: true,
        })
      }

      const params = new URLSearchParams(location.search)
      params.delete('formSubmissionPaymentId')
      if (formSubmissionResult.externalId) {
        params.append('trackingCode', formSubmissionResult.externalId)
      }
      if (formSubmissionResult.previousFormSubmissionApprovalId) {
        params.append(
          'previousFormSubmissionApprovalId',
          formSubmissionResult.previousFormSubmissionApprovalId,
        )
      }
      history.replace({ search: params.toString() })
    } catch (e) {
      setCancelError((e as Error).message)
    }

    setIsCancelling(false)
  }, [location.search, history])

  return (
    <Dialog isOpen={!!formSubmissionPaymentId} compact>
      <PaymentContainer>
        {!errorMessage && !cancelError && (
          <>
            <PaymentHeading>Make a payment</PaymentHeading>
            {isCancelling ? (
              <LoadingSpinner />
            ) : (
              <PaymentForm
                captchaType={recaptchaKeyType}
                captchaSiteKey={recaptchaPublicKey}
                onCompleted={handleCompleted}
                onCancelled={handleCancel}
              />
            )}
          </>
        )}
        {!!errorMessage && (
          <>
            <PaymentHeading $centered>Payment Failed</PaymentHeading>
            <ErrorContainer>
              <ErrorIcon $size="large" />
              <P>{errorMessage}</P>

              <ButtonContainer>
                <button
                  type="button"
                  className="button ob-button is-light"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="button ob-button is-primary"
                  onClick={handleTryAgain}
                >
                  Try again
                </button>
              </ButtonContainer>
            </ErrorContainer>
            {isTryingAgain && (
              <LoadingSpinner>Initialising Payment</LoadingSpinner>
            )}
            {tryAgainError && (
              <ErrorBox>
                <Alert>
                  <ErrorIcon $size="small" />
                  <span>{tryAgainError.message}</span>
                </Alert>
              </ErrorBox>
            )}
          </>
        )}
        {!!cancelError && (
          <>
            <PaymentHeading $centered>Cancel Payment Failed</PaymentHeading>
            <ErrorContainer>
              <ErrorIcon $size="large" />
              <P>{cancelError}</P>

              <ButtonContainer>
                <button
                  type="button"
                  className="button ob-button is-primary"
                  onClick={handleCancel}
                >
                  Try again
                </button>
              </ButtonContainer>
            </ErrorContainer>
            {isTryingAgain && (
              <LoadingSpinner>Initialising Payment</LoadingSpinner>
            )}
            {tryAgainError && (
              <ErrorBox>
                <Alert>
                  <ErrorIcon $size="small" />
                  <span>{tryAgainError.message}</span>
                </Alert>
              </ErrorBox>
            )}
          </>
        )}
      </PaymentContainer>
    </Dialog>
  )
}
